import { useState, useEffect, useMemo } from "react";
import { Modal, Input } from "antd";
import { SearchOutlined, CloseOutlined, StarFilled } from "@ant-design/icons";
import EstimateIcon from "../../../../assets/icons/EstimateIcon.svg";
import ExpIcon from "../../../../assets/icons/ExpIcon.svg";
import LocIcon from "../../../../assets/icons/LocIcon.svg";
import "./SelectJobModal.css";
import "../../QueryPage.css";

const getDisplayValue = (value) => {
  if (typeof value === "string") return value.trim() ? value : "N/A";
  return value != null ? value : "N/A";
};

const getEstimatedAmount = (listing) => {
  if (listing?.MinimumBid) return `$${listing.MinimumBid}/${listing.MinimumBidType || ""}`;
  if (listing?.ClientRate) return `$${listing.ClientRate}/${listing?.BudgetTypes?.BudgetTypeName || ""}`;
  return null;
};

const SelectSkillListingModal = ({ open, onClose, onSelect, selected, options = [], talent, zIndex = 1010 }) => {
  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState(selected || null);

  useEffect(() => {
    if (open) setLocalSelected(selected || null);
  }, [open, selected]);

  const filteredOptions = useMemo(() => {
    const activeOptions = options.filter((o) => {
      const statusName = o?.SkillListingStatuses?.SkillListingStatusName;
      return statusName !== "Deleted" && statusName !== "Expired" && statusName !== "Closed";
    });
    if (!search.trim()) return activeOptions;
    const term = search.trim().toLowerCase();
    return activeOptions.filter((o) => (o.Title || "").toLowerCase().includes(term));
  }, [options, search]);

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  const handleCardClick = (listing) => {
    setLocalSelected(listing);
    onSelect(listing);
    handleClose();
  };

  const initials =
    talent?.initials ||
    (talent?.name || "")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      className="sjm-modal"
      width="min(984px, 95vw)"
      centered
      zIndex={zIndex}
      destroyOnClose
    >
      <div className="sjm-header">
        <span className="sjm-title">Select a Skill Listing</span>
        <button className="sjm-close-btn" type="button" onClick={handleClose}>
          <CloseOutlined />
        </button>
      </div>

      {options.length > 4 && (
        <div className="sjm-toolbar">
          <Input
            prefix={<SearchOutlined className="sjm-search-icon" />}
            placeholder="Search skill listings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="sjm-search"
            allowClear
          />
        </div>
      )}

      <div
        className="qp-cards-grid"
        style={{
          marginBottom: 24,
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        }}
      >
        {filteredOptions.length === 0 ? (
          <div className="sjm-empty">No skill listings found.</div>
        ) : (
          filteredOptions.map((listing) => {
            const isSelected = localSelected?.SkillListingId === listing.SkillListingId;
            const rating = listing?.Profiles?.Accounts?.Rating ?? 0;
            const reviewCount = listing?.Profiles?.Accounts?.Review ?? 0;
            const experience = listing?.Profiles?.ExperienceLevelName;
            const location = listing?.SkillListings_Cities?.[0]?.Cities?.LocationWithCountry;
            const status = listing?.SkillListingStatuses?.SkillListingStatusName === "Published" ? "available" : "unavailable";

            return (
              <div
                key={listing.SkillListingId}
                className={`qp-card qp-card-marketplace qp-card-selectable${isSelected ? " qp-card-selected" : ""}`}
                onClick={() => handleCardClick(listing)}
              >
                <div className={`qp-available-header qp-status-${status}`}>
                  <span className="qp-available-dot" />
                  <span className="qp-available-text">
                    {status === "available" ? "Available" : listing?.SkillListingStatuses?.SkillListingStatusName || "—"}
                  </span>
                </div>

                <div className="qp-card-body-inner">
                  <div className="qp-card-top">
                    <div className="qp-avatar-wrap">
                      {talent?.avatar ? (
                        <img src={talent.avatar} alt={talent?.name} className="qp-avatar" />
                      ) : (
                        <div className="qp-avatar-placeholder">{initials}</div>
                      )}
                    </div>
                    <div className="qp-card-info">
                      <div className="qp-card-name">{listing.Title || "Untitled"}</div>
                      <div className="qp-card-title">#{listing.SkillListingId}</div>
                      <div className="qp-card-rating-row">
                        <StarFilled className="qp-star-icon" />
                        <span className="qp-rating-value">{rating}</span>
                        <span className="qp-rating-count">({reviewCount})</span>
                        <span className="qp-rating-divider">|</span>
                        <span className="qp-tag-pmt">PMT</span>
                      </div>
                    </div>
                  </div>

                  <div className="qp-card-details">
                    <div className="qp-detail-row">
                      <span className="qp-detail-label">
                        <span className="qp-detail-icon"><img src={EstimateIcon} alt="estimate" /></span>
                        Estimated Amount:
                      </span>
                      <span className="qp-detail-value">{getDisplayValue(getEstimatedAmount(listing))}</span>
                    </div>
                    <div className="qp-detail-row">
                      <span className="qp-detail-label">
                        <span className="qp-detail-icon"><img src={ExpIcon} alt="experience" /></span>
                        Experience:
                      </span>
                      <span className="qp-detail-value">{getDisplayValue(experience)}</span>
                    </div>
                    <div className="qp-detail-row">
                      <span className="qp-detail-label">
                        <span className="qp-detail-icon"><img src={LocIcon} alt="location" /></span>
                        Location
                      </span>
                      <span className="qp-detail-value">{getDisplayValue(location)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
};

export default SelectSkillListingModal;
